import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { ScrollArea } from "../ui/scroll-area";
import { ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";

const DEFAULT_TERMS = `Trading Alert LMS - Terms of Service

Risk Disclaimer: Trading in financial markets involves substantial risk of loss. 
The alerts provided are for informational purposes only and do not constitute financial advice.

1. Service Description
This platform provides trading alerts generated by automated signal systems. These alerts are 
delivered in real-time and include entry points, stop-loss levels, and take-profit targets.

2. No Financial Advice
The information provided through this service does not constitute investment advice, financial 
advice, trading advice, or any other sort of advice. You should not treat any of the content 
as such. We do not recommend that any financial instrument should be bought, sold, or held by you.

3. Risk Acknowledgment
By using this service, you acknowledge that:
- Trading involves significant risk of loss
- Past performance does not guarantee future results
- You may lose more than your initial investment
- You are solely responsible for your trading decisions
- You should only trade with money you can afford to lose

4. No Guarantees
We make no representations or warranties about the accuracy, completeness, or timeliness of 
the alerts. Trading signals may be delayed, incorrect, or fail to arrive. We are not liable 
for any losses resulting from the use of this service.

5. User Responsibilities
You are responsible for:
- Conducting your own research before making any trading decisions
- Managing your own risk and position sizing
- Ensuring compliance with applicable laws and regulations
- Maintaining the security of your account

6. Limitation of Liability
Under no circumstances shall we be liable for any direct, indirect, incidental, special, 
consequential, or punitive damages resulting from your use of this service or any trading 
decisions made based on the alerts provided.

7. Modifications
We reserve the right to modify these terms at any time. Continued use of the service after 
any modifications constitutes acceptance of the updated terms.

8. Agreement
By clicking "I Agree" below, you confirm that you have read, understood, and agree to be 
bound by these Terms & Conditions.`;

export default function TermsModal({ open, onAccept, isLoading, termsContent }) {
    const [agreed, setAgreed] = useState(false);

    const displayContent = termsContent || DEFAULT_TERMS;

    return (
        <Dialog open={open} onOpenChange={() => { }}>
            <DialogContent
                className="bg-slate-900 border-slate-800 max-w-2xl"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                            <ShieldCheck className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl text-white">Terms & Conditions</DialogTitle>
                            <DialogDescription className="text-slate-400">
                                Please read and accept to continue
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <ScrollArea className="h-[400px] rounded-lg border border-slate-800 bg-slate-950 p-4">
                    <div className="text-slate-300 space-y-4 text-sm leading-relaxed">
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                            <p className="text-amber-200">
                                <strong>Risk Disclaimer:</strong> Trading in financial markets involves substantial risk of loss.
                                The alerts provided are for informational purposes only and do not constitute financial advice.
                            </p>
                        </div>

                        <div className="whitespace-pre-wrap">
                            {displayContent}
                        </div>
                    </div>
                </ScrollArea>

                <div className="flex items-center gap-3 py-2">
                    <Checkbox
                        id="terms-agree"
                        checked={agreed}
                        onCheckedChange={setAgreed}
                        className="w-5 h-5 border-2 border-slate-300 bg-slate-100 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white data-[state=checked]:border-blue-600"
                    />
                    <label
                        htmlFor="terms-agree"
                        className="text-sm text-slate-300 cursor-pointer select-none"
                    >
                        I have read and agree to the Terms & Conditions and Risk Disclaimer
                    </label>
                </div>

                <DialogFooter>
                    <Button
                        onClick={onAccept}
                        disabled={!agreed || isLoading}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <ShieldCheck className="w-4 h-4 mr-2" />
                                I Agree - Continue to Dashboard
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
